import * as antlr4 from 'antlr4ng';

import {
  type ActiveCohortBuilderItemLegacy,
  type ActiveIndexDateItemLegacy,
  type CohortBuilderDefinitionLegacy,
  CohortBuilderModalLegacy,
  type InputOptionContextDataLegacy,
} from '@verantos-dev/types-cohorts';
import { parseCohortDefinitionQuery } from '@verantos-dev/util-cohort-builder-typescript-runtime';

import { getActiveCohortBuilderItemStateForDerivedEvent } from './utils/getActiveCohortBuilderItemStateForDerivedEvent';
import { getActiveCohortBuilderItemStateForIndexDate } from './utils/getActiveCohortBuilderItemStateForIndexDate';
import { getActiveCohortBuilderItemStateForSimpleEvent } from './utils/getActiveCohortBuilderItemStateForSimpleEvent';
import { getActiveCohortBuilderItemStateForSubjectVariable } from './utils/getActiveCohortBuilderItemStateForSubjectVariable';
import { getCohortDSLValueForModalType } from './utils/getCohortDSLValueForModalType';
import { getRequiredDataFromState } from './utils/getRequiredDataFromState';
import { getCurrentCohortEventNames } from '../utils/getCurrentCohortEventNames';
import { getExpectedTokensForTokenStream } from './utils/getExpectedTokensForTokenStream';

/**
 * @description This holds the main logic for the generating the next active cohort builder item state:
 *
 * The goal of this function is to generate the next active cohort builder item state.
 *
 * ANTLR has difficulty parsing multiple lines at once that are incomplete. Therefore, we will only
 * generate and parse the DSL for the active cohort builder item plus criteria portions.
 * This will allow us to generate code completion options for the active cohort builder item.
 *
 * 1. UI to DSL Translation: Transform the UI cohort definition model to the DSL for the active cohort builder item
 * 2. Parse Cohort Builder DSL: Pass the DSL into the Antlr runtime, obtain the token stream and parse tree
 * 3. Generate Code Completion Options: Parse Obtain the expected tokens for the token stream
 * 4. Link Code Completion to UI model: Map the expected tokens to the UI cohort definition model
 * 5. Input Field Generation: Update the UI cohort definition model with latest options and input fields
 *
 * TODO: @chris.may: Add error handling
 *
 * @param {CohortBuilderDefinition} cohortDefinition
 * @param {CohortBuilderWorkflow} cohortBuilderWorkflow
 * @param {ActiveCohortBuilderItem} nextActiveCohortBuilderItem
 *
 * @return {ActiveCohortBuilderItem} The next active cohort builder item
 */
const getNextActiveCohortBuilderItemState = (
  modal: CohortBuilderModalLegacy | undefined,
  cohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  /**
   * Validate that the cohort definition and workflow exist
   */
  if (!cohortDefinition) {
    throw new Error(
      `The cohort definition must be defined.\ncohortDefinition: ${cohortDefinition}`
    );
  }

  /**
   * Get the required data from the state to perform calculations on the next active cohort builder item
   */
  const {
    /** The value of the current cohort builder item in the definition */
    currentSubjectVariable,
    currentDerivedEvent,
    currentSimpleEvent,

    /** data from cohortDefinition */
    subjectVariables,
    simpleEvents,
    derivedEvents,

    /** data from nextActiveCohortBuilderItem */
    id,
    name,
    type,
  } = getRequiredDataFromState(cohortDefinition, nextActiveCohortBuilderItem, modal);

  /**
   * Begin the runtime check to generate options and validate the next active cohort builder item
   */
  if (modal !== undefined) {
    // only enter runtime if there is an active modal defined

    /**
     * Runtime check calculations
     * We only want to execute the runtime once some preliminary values have been entered by the user
     */
    const nameCheck =
      name || // Name must be defined
      [CohortBuilderModalLegacy.ADD_INDEX_DATE, CohortBuilderModalLegacy.EDIT_INDEX_DATE].includes(
        modal
      ); // Name is not required for index date
    const typeCheck = type; // Type must be defined
    if (nameCheck && typeCheck) {
      /**
       * 1. UI to DSL Translation
       * */
      const newCohortDslValue = getCohortDSLValueForModalType(
        modal,
        cohortDefinition,
        nextActiveCohortBuilderItem
      );

      /**
       * 2. Parse Cohort Builder DSL
       * */
      const { cohortDefinitionParser, tokenStream, cohortDefinitionTree } =
        parseCohortDefinitionQuery(newCohortDslValue);

      /**
       * 3. Generate Code Completion Options
       * */
      const tokens = tokenStream.getTokens();
      const codeCompletionOptions = getExpectedTokensForTokenStream(tokens, cohortDefinitionParser);

      // Added below logic to throw error if there is mismatch in token generated and code completion options
      // This happens when there is an error in the DSL
      for (let i = 0; i < tokens.length; i++) {
        if (codeCompletionOptions[i].length === 0 && tokens[i].type !== antlr4.Token.EOF) {
          console.error(
            `There is an error for token ${tokens[i].tokenIndex}:"${tokens[i].text}" in line:${
              tokens[i]?.line || ''
            } and column: ${tokens[i]?.column || ''}. codeCompletionOptions for token, ${
              tokens[i]?.text || ''
            } is empty. \nTokens: ${tokens
              .map((token) => `${token.line}:${token.text}`)
              .join(', ')}`
          );
          break;
        }
      }

      /** 4.
       * Link Code Completion to UI model
       * */

      /**
       * @description
       * Goal: Map subject variable code completion options to the UI model
       *
       * Prerequisites:
       * - successfully parsed subject variable contexts from the cohort definition tree
       * - subject variables in the UI model, or a new subject variable being added
       *
       *
       * First we must obtain the array of subject variables context from the parse tree
       * We take the parse tree (cohortDefinitionTree) and look for the start of the subject variables contexts
       * We then check the number of subject variables in the UI model to determine the end index
       * We then slice the array of children to obtain the subject variables contexts
       */

      if (cohortDefinitionTree.children) {
        switch (modal) {
          case CohortBuilderModalLegacy.ADD_SUBJECT_VARIABLE: {
            const numberOfSubjectVariables = subjectVariables.length + 1;
            const subjectVariablesContexts = cohortDefinitionTree.subjects_variable();
            const currentSubjectVariableContext =
              subjectVariablesContexts[subjectVariablesContexts.length - 1];
            // Sanity check: Validate that the number of subject variables in the UI model matches the number of subject variables in the parse tree
            if (subjectVariablesContexts.length !== numberOfSubjectVariables) {
              throw new Error(
                `Number of subject variables in UI model does not match number of subject variables in parse tree.\nsubjectVariablesContexts (${
                  subjectVariablesContexts.length
                }): ${subjectVariablesContexts.map((context) =>
                  context.getText()
                )}\nsubjectVariables (${numberOfSubjectVariables}): ${subjectVariables.map(
                  (variable) => variable.name
                )}`
              );
            }

            return getActiveCohortBuilderItemStateForSubjectVariable(
              currentSubjectVariableContext,
              codeCompletionOptions,
              nextActiveCohortBuilderItem,
              inputOptionContextData
            );
          }
          case CohortBuilderModalLegacy.EDIT_SUBJECT_VARIABLE: {
            if (!currentSubjectVariable) {
              throw new Error(`Subject variable was not found for ${id}`);
            }
            const numberOfSubjectVariables = subjectVariables.length;
            const currentSubjectVariableIndex = subjectVariables.findIndex(
              (variable) => variable.id === id
            );
            const subjectVariablesContexts = cohortDefinitionTree.subjects_variable();
            const currentSubjectVariableContext =
              subjectVariablesContexts[currentSubjectVariableIndex];

            // Sanity check: Validate that the number of subject variables in the UI model matches the number of subject variables in the parse tree
            if (subjectVariablesContexts.length !== numberOfSubjectVariables) {
              throw new Error(
                `Number of subject variables in UI model does not match number of subject variables in parse tree.\nsubjectVariablesContexts (${
                  subjectVariablesContexts.length
                }): ${subjectVariablesContexts.map((context) =>
                  context.getText()
                )}\nsubjectVariables (${numberOfSubjectVariables}): ${subjectVariables.map(
                  (variable) => variable.name
                )}`
              );
            }

            return getActiveCohortBuilderItemStateForSubjectVariable(
              currentSubjectVariableContext,
              codeCompletionOptions,
              nextActiveCohortBuilderItem,
              inputOptionContextData
            );
          }

          case CohortBuilderModalLegacy.ADD_INDEX_DATE:
          case CohortBuilderModalLegacy.EDIT_INDEX_DATE: {
            const indexDateContext = cohortDefinitionTree.index_date();

            if (!indexDateContext) {
              throw new Error(`ADD/EDIT_INDEX_DATE: Index date context was not found for ${id}`);
            }
            const eventNames = getCurrentCohortEventNames(
              nextActiveCohortBuilderItem,
              cohortDefinition
            );

            return getActiveCohortBuilderItemStateForIndexDate(
              indexDateContext,
              codeCompletionOptions,
              nextActiveCohortBuilderItem as ActiveIndexDateItemLegacy,
              {
                ...inputOptionContextData,
                eventNames,
              }
            );
          }

          case CohortBuilderModalLegacy.ADD_DERIVED_EVENT: {
            const numberOfDerivedEvents = derivedEvents.length + 1;
            const derivedEventsContexts = cohortDefinitionTree.derived_event();
            const currentDerivedEventContext =
              derivedEventsContexts[derivedEventsContexts.length - 1];

            // Sanity check: Validate that the number of subject variables in the UI model matches the number of subject variables in the parse tree
            if (derivedEventsContexts.length !== numberOfDerivedEvents) {
              throw new Error(
                `Number of derived events in UI model does not match number of derived events in parse tree.\nderivedEventContexts (${
                  derivedEventsContexts.length
                }): ${derivedEventsContexts.map((context) =>
                  context.getText()
                )}\nderivedEvents (${numberOfDerivedEvents}): ${simpleEvents.map(
                  (variable) => variable.name
                )}`
              );
            }

            return getActiveCohortBuilderItemStateForDerivedEvent(
              currentDerivedEventContext,
              codeCompletionOptions,
              nextActiveCohortBuilderItem,
              inputOptionContextData
            );
          }

          case CohortBuilderModalLegacy.EDIT_DERIVED_EVENT: {
            if (!currentDerivedEvent) {
              throw new Error(`Derived event was not found for ${id}`);
            }
            const numberOfDerivedEvents = derivedEvents.length;
            const currentSimpleEventIndex = derivedEvents.findIndex(
              (variable) => variable.id === id
            );
            const derivedEventsContexts = cohortDefinitionTree.derived_event();
            const currentDerivedEventContext = derivedEventsContexts[currentSimpleEventIndex];

            // Sanity check: Validate that the number of subject variables in the UI model matches the number of subject variables in the parse tree
            if (derivedEventsContexts.length !== numberOfDerivedEvents) {
              throw new Error(
                `Number of derived events in UI model does not match number of derived events in parse tree.\nderivedEventContexts (${
                  derivedEventsContexts.length
                }): ${derivedEventsContexts.map((context) =>
                  context.getText()
                )}\nderivedEvents (${numberOfDerivedEvents}): ${simpleEvents.map(
                  (variable) => variable.name
                )}`
              );
            }

            return getActiveCohortBuilderItemStateForDerivedEvent(
              currentDerivedEventContext,
              codeCompletionOptions,
              nextActiveCohortBuilderItem,
              inputOptionContextData
            );
          }

          case CohortBuilderModalLegacy.ADD_SIMPLE_EVENT: {
            const numberOfSimpleEvents = simpleEvents.length + 1;
            const simpleEventsContexts = cohortDefinitionTree.simple_event();
            const currentSimpleEventContext = simpleEventsContexts[simpleEventsContexts.length - 1];
            // Sanity check: Validate that the number of simple events in the UI model matches the number of simple events in the parse tree
            if (simpleEventsContexts.length !== numberOfSimpleEvents) {
              throw new Error(
                `Number of simple events in UI model does not match number of simple events in parse tree.\nsimpleEventsContexts (${
                  simpleEventsContexts.length
                }): ${simpleEventsContexts.map((context) =>
                  context.getText()
                )}\nsimpleEvents (${numberOfSimpleEvents}): ${simpleEvents.map(
                  (variable) => variable.name
                )}`
              );
            }

            return getActiveCohortBuilderItemStateForSimpleEvent(
              currentSimpleEventContext,
              codeCompletionOptions,
              nextActiveCohortBuilderItem,
              inputOptionContextData
            );
          }
          case CohortBuilderModalLegacy.EDIT_SIMPLE_EVENT: {
            if (!currentSimpleEvent) {
              throw new Error(`Simple event was not found for ${id}`);
            }
            const numberOfSimpleEvents = simpleEvents.length;
            const currentSimpleEventIndex = simpleEvents.findIndex((sv) => sv.id === id);
            const simpleEventsContexts = cohortDefinitionTree.simple_event();
            const currentSimpleEventContext = simpleEventsContexts[currentSimpleEventIndex];
            // Sanity check: Validate that the number of simple events in the UI model matches the number of simple events in the parse tree
            if (simpleEventsContexts.length !== numberOfSimpleEvents) {
              throw new Error(
                `Number of simple events in UI model does not match number of simple events in parse tree.\nsimpleEventsContexts (${
                  simpleEventsContexts.length
                }): ${simpleEventsContexts.map((context) =>
                  context.getText()
                )}\nsimpleEvents (${numberOfSimpleEvents}): ${simpleEvents.map(
                  (variable) => variable.name
                )}`
              );
            }
            return getActiveCohortBuilderItemStateForSimpleEvent(
              currentSimpleEventContext,
              codeCompletionOptions,
              nextActiveCohortBuilderItem,
              inputOptionContextData
            );
          }
        }
      }
    }
  }

  return nextActiveCohortBuilderItem;
};

/**
 * Finds the next active cohort builder item state
 *
 * @param {CohortBuilderDefinition} cohortDefinition
 * @param {CohortBuilderWorkflow} cohortBuilderWorkflow
 * @param {ActiveCohortBuilderItem} nextActiveCohortBuilderItem
 *
 * @returns {ActiveCohortBuilderItem} The next active cohort builder item
 */
export const recursiveCheckNextActiveCohortBuilderItemState = (
  modal: CohortBuilderModalLegacy | undefined,
  cohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  const _nextActiveCohortBuilderItem = getNextActiveCohortBuilderItemState(
    modal,
    cohortDefinition,
    nextActiveCohortBuilderItem,
    inputOptionContextData
  );
  if (
    JSON.stringify(nextActiveCohortBuilderItem) !== JSON.stringify(_nextActiveCohortBuilderItem)
  ) {
    return recursiveCheckNextActiveCohortBuilderItemState(
      modal,
      cohortDefinition,
      _nextActiveCohortBuilderItem,
      {
        ...inputOptionContextData,
        activeCohortBuilderItem: _nextActiveCohortBuilderItem,
      }
    );
  }
  return _nextActiveCohortBuilderItem;
};
